import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  institutionLogin,
  institutionSignup,
  institutionMe,
  getToken,
  clearToken,
} from "../api/client";
import { clearInstitutionDashboardCache } from "../api/dashboard-cache";

// Shared auth/session state for the institution portal. Hydrates from the
// backend on load (if a token is present) so a refresh keeps the admin signed
// in. institution_id is never stored client-side as an authority — it's always
// re-derived server-side; here we only keep display fields.
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [institution, setInstitution] = useState(null);
  const [loading, setLoading] = useState(true);

  const hydrate = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setInstitution(null);
      setLoading(false);
      return;
    }
    try {
      const data = await institutionMe();
      setUser(data.user || null);
      setInstitution(data.institution || null);
    } catch {
      // Token invalid/expired — the API layer already cleared it on 401.
      clearToken();
      setUser(null);
      setInstitution(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const login = useCallback(async (credentials) => {
    const data = await institutionLogin(credentials);
    setUser(data.user || null);
    setInstitution(data.institution || null);
    return data;
  }, []);

  const signup = useCallback(async (payload) => {
    const data = await institutionSignup(payload);
    setUser(data.user || null);
    setInstitution(data.institution || null);
    return data;
  }, []);

  const logout = useCallback(() => {
    const token = getToken();
    clearToken();
    void clearInstitutionDashboardCache(token);
    setUser(null);
    setInstitution(null);
  }, []);

  // patchInstitution merges display fields (e.g. subdomain, logoUrl) into the
  // cached institution after a settings/logo change, so the co-branded shell
  // updates immediately without a full /me round-trip.
  const patchInstitution = useCallback((fields) => {
    setInstitution((prev) => (prev ? { ...prev, ...fields } : prev));
  }, []);

  const value = {
    user,
    institution,
    loading,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    refresh: hydrate,
    patchInstitution,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}

export default AuthContext;
